import axios, { AxiosInstance } from "axios";
import {
    AssociatedAccount,
    SubAccountPurchasesPageResponse,
    Department,
    ReleaseNote,
    ReleaseNoteMonth,
    DirectLink,
    Distributor,
    Email,
    Identifier,
    LanguageProduct,
    LinkedAccountData,
    MonthlyPurchase,
    MonthlyTransfer,
    Participant,
    ParticipantActivity,
    ParticipantActivityType,
    Participants,
    Product,
    Project,
    PagedProjects,
    ProjectsPageRequest,
    ProfileRetrieval,
    PurchasedProduct,
    SubAccountPurchasesPageRequest,
    SubAccountsPageRequest,
    SubAccountsPageResponse,
    Trainer,
    ProjectRole,
    Questionnaire,
    AssociatedDirectInput,
    DirectInput,
    ProductCalculationResults,
    CrossSell,
    Role,
    GroupChart, GroupChartSummary, ClientInfo,
    Transaction,
    Dependency,
    ShortLinkRequest
} from "../types/PersologOnlineTypes.ts";
import {
    BannerAudience,
    bannerImageUrl,
    extractPdfFilenameFromHeaders,
    NotificationBannerItem,
    StartPageBannerItem,
} from "@persolog/utils";
import { keycloak } from "../main.ts";
import { getImpersonatedAccount } from "../utils/GeneralUtils.ts";

// Identifies this app as the origin of a request, so a failing backend request can be
// traced back here in the logs (sent as the X-Client header on every call).
const CLIENT_APP = "profile-administration-frontend";
const newRequestId = (): string =>
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const backendBaseUrl = import.meta.env.VITE_APP_BACKEND_API_BASE_URL;
const profileAdministrationPath = import.meta.env.VITE_APP_PROFILE_ADMINISTRATION_PATH;
const languageManagementPath = import.meta.env.VITE_APP_LANGUAGE_MANAGEMENT_PATH;
const profileContractorPath = import.meta.env.VITE_APP_PROFILE_CONTRACTOR_PATH;
const inDesignTemplate = import.meta.env.VITE_APP_IN_DESIGN_TEMPLATE;
const directInputPath = import.meta.env.VITE_APP_DIRECT_INPUT_PATH;
const calculatorPath = import.meta.env.VITE_APP_CALCULATOR_PATH;
const questionnairePath = import.meta.env.VITE_APP_QUESTIONNAIRE_PATH;
const emailServicePath = import.meta.env.VITE_APP_EMAIL_SERVICE_PATH;
const trainerAdminBaseUrl = import.meta.env.VITE_APP_TRAINER_ADMINISTRATION_API_BASE_URL;

const profileAdminAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${profileAdministrationPath}`,
    headers: {
        Accept: "application/json",
    },
});

/**
 * IMPORTANT: This axios instance is intended for public (unauthenticated) access to the Profile Administration API.
 *  */
const publicProfileAdminAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${profileAdministrationPath}`,
    headers: {
        Accept: "application/json",
    },
});

const publicLanguageManagementAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${languageManagementPath}`,
    headers: {
        Accept: "application/json",
    },
});

const languageManagementAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${languageManagementPath}`,
    headers: {
        Accept: "application/json",
    },
});

const profileContractorAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${profileContractorPath}`,
    headers: {
        Accept: "application/json",
    },
});

const questionnaireAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${questionnairePath}`,
    headers: {
        Accept: "application/json",
    },
});

const calculatorAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${calculatorPath}`,
    headers: {
        Accept: "application/json",
    },
});

const emailServiceAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${emailServicePath}`,
    headers: {
        Accept: "application/json",
    },
});

const directInputAxiosInstance = axios.create({
    baseURL: `${backendBaseUrl}${directInputPath}`,
    headers: {
        Accept: "application/json",
    },
});

const trainerAdminAxiosInstance = axios.create({
    baseURL: `${trainerAdminBaseUrl}`,
    headers: {
        Accept: "application/json",
        token: import.meta.env.VITE_APP_TRAINER_ADMIN_TOKEN,
    },
});

function setupInterceptor(apiInstance: AxiosInstance) {
    apiInstance.interceptors.request.use(
        async (config) => {
            try {
                await keycloak.updateToken(30);

                if (keycloak.token) {
                    config.headers.Authorization = `Bearer ${keycloak.token}`;
                }

            } catch (err) {
                console.error("Token refresh failed, redirecting to login", err);
                const tokenError = new Error("Token expired and refresh failed");
                (tokenError as Error & { cause?: unknown }).cause = err;
                throw tokenError;
            }

            config.headers["X-Request-Id"] = newRequestId();
            config.headers["X-Client"] = CLIENT_APP;

            return config;
        },
        (error: Error) => {
            return Promise.reject(error);
        }
    );
}

setupInterceptor(profileAdminAxiosInstance);
setupInterceptor(languageManagementAxiosInstance);
setupInterceptor(profileContractorAxiosInstance);
setupInterceptor(questionnaireAxiosInstance);
setupInterceptor(calculatorAxiosInstance);
setupInterceptor(emailServiceAxiosInstance);
setupInterceptor(directInputAxiosInstance);

export interface AuthenticatedPdfDownloadResult {
    blob: Blob;
    filename: string;
}


export const ProfileAdministrationAPI = {
    async fetchPurchasedProducts(trainerId: string): Promise<PurchasedProduct[]> {
        const { data } = await profileAdminAxiosInstance.get(`/products/${trainerId}`);
        return data.purchasedProducts;
    },

    /**
     * Fetches all products from the backend (only for internal use)
     * @deprecated Use fetchAllProductsForTrainer instead
     * @returns list of all products
     */
    async fetchProducts(): Promise<Product[]> {
        const { data } = await profileAdminAxiosInstance.get("/products");
        return data.products;
    },

    /**
     * Fetches all public products from the backend
     * @returns list of public products
     */
    async fetchPublicProducts(): Promise<Product[]> {
        const { data } = await publicProfileAdminAxiosInstance.get("/products/public");
        return data.products;
    },

    /**
     * Group charts with the products they aggregate, for the public Language Overview (RDT-403).
     * Group charts are not rows in the products table, so they have to be fetched separately.
     * Uses the public (interceptor-free) instance: an anonymous visitor has no Keycloak token.
     */
    async fetchPublicGroupCharts(): Promise<GroupChart[]> {
        const { data } = await publicProfileAdminAxiosInstance.get("/group-chart");
        return data.groupCharts;
    },

    /**
     * Customer-facing release notes for the public page (RDT-286), newest month first. Uses the
     * public (interceptor-free) instance: an anonymous visitor has no Keycloak token to refresh.
     * Both languages come down together so the DE/EN switch needs no second request.
     */
    async fetchPublicReleaseNotes(months?: number): Promise<ReleaseNoteMonth[]> {
        const { data } = await publicProfileAdminAxiosInstance.get("/release-notes/public", {
            params: months ? { months } : undefined,
        });
        return data.months ?? [];
    },

    /**
     * New features this trainer has not been shown yet, for the feature popup (RDT-286). The backend
     * caps the list and returns features only, so nothing here decides what is "not overwhelming".
     */
    async fetchNewReleaseNotes(trainerId: string): Promise<ReleaseNote[]> {
        const { data } = await profileAdminAxiosInstance.get(`/release-notes/new/${trainerId}`);
        return data.releaseNotes ?? [];
    },

    /** Records that the trainer has now seen the release notes, so the popup does not repeat. */
    async markReleaseNotesSeen(trainerId: string): Promise<void> {
        await profileAdminAxiosInstance.post(`/trainer/${trainerId}/release-notes-seen`);
    },

    /**
     * Fetches all trainer's products from the backend, including distributor-specific and previously purchased ones.
     * Unavailable products are included if previously purchased.
     * @param trainerId
     * @returns list of avaliable products for the trainer
     */
    async fetchAllProductsForTrainer(trainerId: string): Promise<Product[]> {
        const { data } = await profileAdminAxiosInstance.get(`/products?trainer=${trainerId}`);
        return data.products;
    },

    async favoriteProduct(trainer: string, product: string): Promise<void> {
        await profileAdminAxiosInstance.post(`/products/${trainer}/${product}/favorite`);
    },

    async createTrainer(trainer: Trainer): Promise<Trainer> {
        const { data } = await profileAdminAxiosInstance.post(`/trainer`, trainer);
        return data;
    },

    /**
     * The receiving account of a distributor's transfer by e-mail-address: an existing sub-account of
     * the distributor, or the new trainer record of a Keycloak user who never logged in (EP-3513).
     */
    async findOrCreateSubAccount(distributorAccountId: string, email: string): Promise<Trainer> {
        const { data } = await profileAdminAxiosInstance.post(`/trainer/sub-accounts/${distributorAccountId}`, null, {
            params: { email },
        });
        return data;
    },

    async fetchTrainerDetails(trainer: string): Promise<Trainer> {
        const { data } = await profileAdminAxiosInstance.get(`/trainer/${trainer}`);
        return data;
    },

    /**
     * Deletes trainer from database
     * @param trainer
     */
    async hardDeleteTrainer(trainer: string): Promise<void> {
        await profileAdminAxiosInstance.delete(`/trainer/hard-delete/${trainer}`);
    },

    /**
     * Get trainer by email
     * The backend returns a list of all matching trainers, but we only expect one, so we return the first one
     * @param email
     * @returns trainer object
     */
    async getTrainerByEmail(email: string): Promise<Trainer> {
        const encodedEmail = btoa(email);
        const { data } = await profileAdminAxiosInstance.get(`/trainer/email/${encodedEmail}`);
        return data.trainers[0];
    },

    async fetchDistributors(): Promise<Distributor[]> {
        const { data } = await profileAdminAxiosInstance.get(`/trainer/distributors`);
        return data.distributors;
    },

    async setMonthlyBillingDefaultForDistributor(distributorId: number, state: boolean): Promise<void> {
        await profileAdminAxiosInstance.put(`/distributors/${distributorId}`, state, {
            headers: {
                "Content-Type": "application/json",
            },
        });
    },

    async updateTrainerDetails(trainer: Trainer): Promise<Trainer> {
        const { data } = await profileAdminAxiosInstance.put(`/trainer/${trainer.id}`, trainer);
        return data;
    },

    // The purchase date is stamped server-side; the payload deliberately carries none.
    async buyProduct(monthlyPurchase: Omit<MonthlyPurchase, "date">): Promise<MonthlyPurchase> {
        const { data } = await profileAdminAxiosInstance.post(`/purchases/${monthlyPurchase.trainer}`, monthlyPurchase);
        return data;
    },

    async getPurchases(trainer: string | undefined, fromDate?: string, toDate?: string): Promise<MonthlyPurchase[]> {
        let queryParams = "";
        if (fromDate && toDate) {
            queryParams = `?from=${fromDate}&to=${toDate}`;
        }

        const { data } = await profileAdminAxiosInstance.get(`/purchases/${trainer}${queryParams}`);
        return data.monthlyPurchases;
    },

    /**
     * Fetches a paginated list of sub-account purchases for a distributor trainer within a specified date range to manage large datasets efficiently and improve performance.
     * @param request The request object containing the trainer ID, date range, page number, and page size.
     * @returns A promise that resolves to a paginated response containing sub-account purchases.
     */
    async getPagedSubAccountPurchases(request: SubAccountPurchasesPageRequest): Promise<SubAccountPurchasesPageResponse> {
        const { data } = await profileAdminAxiosInstance.get(`/purchases/${request.trainerId}/sub-accounts/paged`, {
            params: {
                from: request.from,
                to: request.to,
                page: request.page,
                size: request.size,
                sort: "date,desc",
            },
        });

        return {
            purchases: data.purchases ?? data.items ?? data.content ?? [],
            page: data.page ?? data.number ?? request.page,
            size: data.size ?? request.size,
            totalElements: data.totalElements ?? data.totalItems ?? 0,
            totalQuantity: data.totalQuantity ?? 0,
            hasNext: data.hasNext ?? !(data.last ?? true),
        };
    },

    async getTransactions(trainer: string | undefined, fromDate?: string, toDate?: string): Promise<Transaction[]> {
        let queryParams = "";
        if (fromDate && toDate) {
            queryParams = `?from=${fromDate}&to=${toDate}`;
        }

        const { data } = await profileAdminAxiosInstance.get(`/transactions/${trainer}/period${queryParams}`);

        return data;
    },


    /**
     * Get the available monthly billing quantity for a trainer
     * @param trainer
     */
    async getAvailableMonthlyLimit(trainerId: string) {
        const { data } = await profileAdminAxiosInstance.get(`/purchases/${trainerId}/available`);
        return data;
    },

    async getTransfers(trainer: string | undefined, fromDate?: string, toDate?: string): Promise<MonthlyTransfer[]> {
        let queryParams = "";
        if (fromDate && toDate) {
            queryParams = `?from=${fromDate}&to=${toDate}`;
        }

        const { data } = await profileAdminAxiosInstance.get(`/transfers/${trainer}${queryParams}`);
        return data.monthlyTransfers;
    },

    async getReceivedTransfers(
        trainer: string | undefined,
        fromDate?: string,
        toDate?: string,
    ): Promise<MonthlyTransfer[]> {
        let queryParams = "";
        if (fromDate && toDate) {
            queryParams = `?from=${fromDate}&to=${toDate}`;
        }

        const { data } = await profileAdminAxiosInstance.get(`/transfers/account/${trainer}${queryParams}`);
        return data.monthlyTransfers;
    },

    async getProjects(trainer: string): Promise<Project[]> {
        const { data } = await profileAdminAxiosInstance.get(`/projects/${trainer}/product/all`);
        return data.projects;
    },

    /**
     * Fetches a single page of lightweight project surfaces for a trainer. Filtering, sorting and
     * paging are performed by the backend; core data is loaded separately when a project is opened.
     */
    async getProjectsPage(request: ProjectsPageRequest): Promise<PagedProjects> {
        const { data } = await profileAdminAxiosInstance.get(`/projects/${request.trainerId}/surface`, {
            params: {
                page: request.page,
                size: request.size,
                sort: request.sort,
                direction: request.direction,
                archived: request.archived ?? false,
                ...(request.products && request.products.length > 0 ? { products: request.products.join(",") } : {}),
                ...(request.search ? { search: request.search } : {}),
                ...(request.from ? { from: request.from } : {}),
                ...(request.to ? { to: request.to } : {}),
            },
        });
        return data;
    },

    /**
     * Fetches compatible projects for moving a participant.
     */
    async fetchCompatibleProjects(trainerId: string, participantId: number): Promise<Project[]> {
        const { data } = await profileAdminAxiosInstance.get(`/projects/${trainerId}/${participantId}/compatible`);
        return data.projects;
    },

    /**
     * Moves a participant to another project.
     */
    async moveParticipant(targetProjectId: number, participantId: number): Promise<Participant> {
        const { data } = await profileAdminAxiosInstance.post(`/participants/${targetProjectId}/move/${participantId}`);
        return data;
    },


    async getProject(trainerId: string, projectId: number): Promise<Project> {
        const { data } = await profileAdminAxiosInstance.get(`/projects/${trainerId}/${projectId}`);
        return data;
    },

    async createProject(project: Project): Promise<Project> {
        const { data } = await profileAdminAxiosInstance.post(`/projects/${project.trainer}/create`, project);
        return data;
    },

    async reactivateProject(projectId: number | undefined): Promise<Project> {
        const { data } = await profileAdminAxiosInstance.post(`/projects/${projectId}/archive`);
        return data;
    },

    async getParticipants(projectId: number): Promise<Participant[]> {
        const { data } = await profileAdminAxiosInstance.get(`participants/${projectId}`);
        return data.participants;
    },

    async addParticipants(projectId: number, participants: Participants): Promise<Participants> {
        const { data } = await profileAdminAxiosInstance.put(`/participants/${projectId}`, participants);
        return data;
    },

    /**
     * Activate a participant
     * @param participantId participant id
     * @returns activated participant
     */
    async activateParticipant(participantId: number): Promise<Participant> {
        const { data } = await profileAdminAxiosInstance.put(`/participants/${participantId}/activate`);
        return data;
    },

    /**
     * Activate all participants in a project with status CREATED
     * @param projectId project id
     * @returns activated participants
     */
    async activateParticipants(projectId: number): Promise<Participant[]> {
        const { data } = await profileAdminAxiosInstance.post(`/participants/${projectId}/activate`);
        return data.participants;
    },

    async createDirectLink(trainerId: string, directLink: DirectLink): Promise<DirectLink> {
        const { data } = await profileAdminAxiosInstance.post(`/direct-links/${trainerId}/create`, directLink);
        return data;
    },

    /**
     * Update direct link
     * @param {DirectLink} directLink
     * @returns {Promise<DirectLink>} updated direct link
     *
     * Refactor suggestion: Consider changing this to a PUT request to adhere to RESTful API principles.
     */
    async updateDirectLink(directLink: DirectLink): Promise<DirectLink> {
        const { data } = await profileAdminAxiosInstance.post(`/direct-links/update`, directLink);
        return data;
    },

    /**
     * Get direct link Object
     * @param {number} projectId
     * @returns {Promise<DirectLink>} direct link object
     */
    async getDirectLink(projectId: number): Promise<DirectLink> {
        const { data } = await profileAdminAxiosInstance.get(`/direct-links/project/${projectId}`);
        return data;
    },

    async createDirectLinkForExistingProject(directLink: DirectLink): Promise<DirectLink> {
        const { data } = await profileAdminAxiosInstance.post(`/direct-links/create`, directLink);
        return data;
    },

    async deleteProject(projectId: number): Promise<void> {
        const { data } = await profileAdminAxiosInstance.delete(`/projects/${projectId}/delete`);
        return data;
    },

    async deleteDirectLink(projectId: number): Promise<void> {
        const { data } = await profileAdminAxiosInstance.delete(`/direct-links/${projectId}`);
        return data;
    },

    /**
     * Create (or replace, on rename) the short link of a direct-link project.
     * @param {number} projectId
     * @param {ShortLinkRequest} request optional custom slug and/or expiry
     * @returns {Promise<DirectLink>} the direct link with its short URL
     */
    async createDirectLinkShortLink(projectId: number, request: ShortLinkRequest): Promise<DirectLink> {
        const { data } = await profileAdminAxiosInstance.post(`/direct-links/project/${projectId}/short-link`, request);
        return data;
    },

    /**
     * Remove the short link of a direct-link project.
     * @param {number} projectId
     * @returns {Promise<DirectLink>} the direct link without a short URL
     */
    async deleteDirectLinkShortLink(projectId: number): Promise<DirectLink> {
        const { data } = await profileAdminAxiosInstance.delete(`/direct-links/project/${projectId}/short-link`);
        return data;
    },

    async updateParticipant(participant: Participant): Promise<Participant> {
        const { data } = await profileAdminAxiosInstance.put(`/participants/${participant.id}/update`, participant);
        return data;
    },

    async updateProject(project: Project): Promise<Project> {
        const { data } = await profileAdminAxiosInstance.post(`/projects/${project?.id}/update`, project);
        return data;
    },

    async deleteParticipant(id: number): Promise<void> {
        const { data } = await profileAdminAxiosInstance.delete(`/participants/${id}`);
        return data;
    },

    /**
     * Get the list of departments for a project
     * @param projectId
     */
    async getDepartments(projectId: number): Promise<Department[]> {
        const { data } = await profileAdminAxiosInstance.get(`/projects/${projectId}/departments`);
        return data.departments;
    },

    /**
     * Update the departments for a project
     * @param projectId
     * @param departments
     * @returns list of updated departments
     */
    async updateDepartments(projectId: number, departments: Department[]): Promise<Department[]> {
        const { data } = await profileAdminAxiosInstance.post(`/projects/${projectId}/departments/update`, {
            departments: departments,
        });
        return data.departments;
    },

    /**
     * Get the list of roles selected for a project
     * @param projectId
     * @returns {Promise<ProjectRole[]>} Array of ProjectRoles
     */
    async getRolesForProject(projectId: number): Promise<ProjectRole[]> {
        const { data } = await profileAdminAxiosInstance.get(`/projects/${projectId}/roles`);
        return data.projectRoles;
    },

    /**
     * Get the list of roles available for a product
     * @param product
     * @returns {Promise<Role[]>} Array of Roles
     */
    async getRoleTypesForProduct(product: string): Promise<Role[]> {
        const { data } = await profileAdminAxiosInstance.get(`/roles/${product}`);
        return data.roles;
    },

    /**
     * Get the list of products with their associated roles
     * @returns {Promise<string[]>} Array of product IDs
     */
    async getProductsWithRoles(): Promise<string[]> {
        const { data } = await profileAdminAxiosInstance.get(`/roles/products`);
        return data;
    },

    /**
     * Update the roles for a project, setting the roles if they exist,
     * or creating them if they don't, using the roles array passed in
     * @param projectId
     * @param roles
     * @returns {Promise<ProjectRole[]>}
     *
     * Warning: Not yet tested
     */
    async updateRolesForProject(projectId: number, roles: ProjectRole[]): Promise<ProjectRole[]> {
        const { data } = await profileAdminAxiosInstance.post(`/projects/${projectId}/roles`, {
            projectRoles: roles,
        });
        return data.projectRoles;
    },

    async getGroupChartForProduct(product: string): Promise<GroupChart[]> {
        const { data } = await profileAdminAxiosInstance.get(`/group-chart/product/${product}`);
        return data.groupCharts;
    },

    async getBaseProductsForGroupChart(groupChart: string): Promise<GroupChart[]> {
        if (groupChart === "") {
            return [];
        }
        const { data } = await profileAdminAxiosInstance.get(`/group-chart/composite/${groupChart}`);
        return data.groupCharts;
    },

    async getAccountsGrantedAccessByTrainer(trainerId: string): Promise<AssociatedAccount[]> {
        const { data } = await profileAdminAxiosInstance.get(`/associated-account/${trainerId}`);
        return data.associatedAccounts;
    },

    async getAccountsAccessibleByTrainer(trainerId: string): Promise<AssociatedAccount[]> {
        const { data } = await profileAdminAxiosInstance.get(`/associated-account/trainer/${trainerId}`);
        return data.associatedAccounts;
    },

    async linkAccount(linkedAccountData: LinkedAccountData): Promise<AssociatedAccount> {
        const { data } = await profileAdminAxiosInstance.put(`/associated-account/trainer`, linkedAccountData);
        return data;
    },

    async unlinkAccount(id: number): Promise<void> {
        const { data } = await profileAdminAxiosInstance.delete(`/associated-account/${id}`);
        return data;
    },

    async isLinkable(trainer: string, encodedEmail: string): Promise<boolean> {
        const { data } = await profileAdminAxiosInstance.post(`/trainer/linkable/${trainer}/${encodedEmail}`);
        return data;
    },

    async getSubAccounts(trainerId: string): Promise<Trainer[]> {
        const { data } = await profileAdminAxiosInstance.get(`/trainer/sub-accounts/all/${trainerId}`);
        return data.trainers;
    },

    async getPagedSubAccounts(request: SubAccountsPageRequest): Promise<SubAccountsPageResponse> {
        const { data } = await profileAdminAxiosInstance.get(`/trainer/sub-accounts/paged/${request.trainerId}`, {
            params: {
                page: request.page,
                size: request.size,
                sort: "lastname,asc",
                ...(request.search ? { search: request.search } : {}),
            },
        });
        return {
            trainers: data.trainers ?? data.content ?? [],
            page: data.page ?? data.number ?? request.page,
            size: data.size ?? request.size,
            totalElements: data.totalElements ?? data.totalItems ?? 0,
        };
    },

    async setMonthlyBilling(accountId: string, state: boolean): Promise<void> {
        const { data } = await profileAdminAxiosInstance.post(
            `/trainer/${accountId}/monthly-billing?monthlyBillingStatus=${state}`,
        );
        return data;
    },

    async setBeta(trainerId: string, state: boolean): Promise<void> {
        const { data } = await profileAdminAxiosInstance.post(`/trainer/${trainerId}/beta?betaStatus=${state}`);
        return data;
    },

    async transferProducts(trainerId: string, accountId: string, product: PurchasedProduct): Promise<PurchasedProduct> {
        // Transfers triggered from persolog online are tagged "manual" so they can be told apart from
        // transfers coming through the API gateway (webshops) in the transfer overview.
        const { data } = await profileAdminAxiosInstance.post(`/products/transfer/${trainerId}/${accountId}`, {
            ...product,
            source: "manual",
        });
        return data;
    },

    /**
     * A distributor takes profiles back from one of its sub-accounts (RDT-113). The backend clamps
     * to the sub-account's stock; the response reports the actually retrieved amount.
     */
    async retrieveProducts(
        distributorId: string,
        accountId: string,
        product: PurchasedProduct,
    ): Promise<ProfileRetrieval> {
        const { data } = await profileAdminAxiosInstance.post(
            `/products/retrieve/${distributorId}/${accountId}`,
            product,
        );
        return data;
    },

    /**
     * Get a given participant's activity
     * @param {number} participantId
     * @returns {Promise<ParticipantActivity[]>} Array of participant activities
     */
    async getParticipantActivity(participantId: number): Promise<ParticipantActivity[]> {
        const { data } = await profileAdminAxiosInstance.get(`/activities/participant/${participantId}`);
        return data.participantActivities;
    },

    /**
     * Log an activity for a participant
     * @param {number} participantId
     * @param {ParticipantActivityType} activityType
     * @returns {Promise<void>}
     */
    async logParticipantActivity(participantId: number, activityType: ParticipantActivityType): Promise<void> {
        // The backend matches the type by name, so the value is stringified explicitly: axios passes a
        // raw string body through untouched, which would send an unquoted (invalid) JSON body.
        const { data } = await profileAdminAxiosInstance.post(
            `/activities/participant/${participantId}`,
            JSON.stringify(activityType),
            {
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );
        return data;
    },

    /**
     * Gets all participants in a trainer's account
     * @param trainerId
     */
    async getAllParticipants(trainerId: string): Promise<Participant[]> {
        const { data } = await profileAdminAxiosInstance.get(`participants/trainer/${trainerId}`);
        return data.participants;
    },

    /**
     * Deletes a trainer's account, including all products, projects and participants
     * @param trainerId
     */
    async deleteAccount(trainerId: string): Promise<void> {
        const { data } = await profileAdminAxiosInstance.delete(`trainer/${trainerId}`);
        return data;
    },

    async getCrossSellProducts(products: string[], language: string): Promise<CrossSell> {
        const { data } = await profileAdminAxiosInstance.get(
            `/products/cross-sell?products=${products.join(",")}&language=${language}`,
        );
        return data;
    },

    async recordBrowserAnalytics(clientInfo: ClientInfo) {
        await profileAdminAxiosInstance.post(
            `trainer/analytics`,
            clientInfo,
        )
    }
};

export const LanguageManagementAPI = {
    async fetchTexts(product: string): Promise<Identifier[]> {
        const { data } = await languageManagementAxiosInstance.get(`/${product}`);
        return data.identifiers;
    },

    /**
     * Notification banners for this app (RDT-339). Goes over the PUBLIC instance on purpose: the
     * banner is shown before login too, and the authenticated instance's interceptor throws for a
     * visitor without a token.
     */
    async fetchBanners(language: string): Promise<NotificationBannerItem[]> {
        const { data } = await publicLanguageManagementAxiosInstance.get(`/banners`, {
            params: { app: "PROFILE_ADMINISTRATION", language },
        });
        // `?? []` is not paranoia: until language-management ships the banner endpoint, `/banners`
        // falls through to its `/{product-id}` text route and answers 200 with `{identifiers: []}`.
        return data.banners ?? [];
    },

    /**
     * Start-page galleria banners for this app (RDT-359). PUBLIC instance for the same reason as the
     * notification banners: the start page is what an anonymous visitor sees first.
     *
     * The image paths the api returns are service-relative ("/banner-images/<hash>.webp") and get
     * prefixed with this app's own base here, so the browser only ever requests them from this
     * origin — the frontends serve `img-src 'self'`, and nginx proxies that prefix to the api (see
     * nginx.conf). Anything unexpected leaves the list empty and the bundled hero shows instead.
     */
    async fetchStartPageBanners(language: string): Promise<StartPageBannerItem[]> {
        const { data } = await publicLanguageManagementAxiosInstance.get(`/start-page-banners`, {
            params: { language },
        });
        const base = import.meta.env.BASE_URL ?? "/";
        return (data.banners ?? []).map(
            (banner: {
                id: number;
                desktopPath: string;
                mobilePath: string;
                alt: string;
                link: string | null;
                audience: BannerAudience;
            }) => ({
                id: banner.id,
                desktopSrc: bannerImageUrl(banner.desktopPath, base),
                mobileSrc: bannerImageUrl(banner.mobilePath, base),
                alt: banner.alt,
                link: banner.link,
                audience: banner.audience,
            }),
        );
    },

    async fetchPublicProductNames(): Promise<Identifier[]> {
        const { data } = await publicLanguageManagementAxiosInstance.get(`/products`);
        return data.identifiers;
    },

    async fetchPublicModelNames(): Promise<Identifier[]> {
        const { data } = await publicLanguageManagementAxiosInstance.get(`/models`);
        return data.identifiers;
    },

    async fetchPublicLanguages(): Promise<LanguageProduct[]> {
        const { data } = await publicLanguageManagementAxiosInstance.get(`/activation/products`);
        return data.products;
    },

    async fetchLanguages(): Promise<LanguageProduct[]> {
        const { data } = await languageManagementAxiosInstance.get(`/activation/products`);
        return data.products;
    },

    /**
     * Fetches a text product and its dependencies,
     * which are the products from which it inherits texts
     * e.g. for profile-in101 it depends on profile-in100
     * @param stage 'STAGING' or 'PROD'
     * @returns
     */
    async getTextDependencies(stage: string): Promise<Dependency[]> {
        const { data } = await languageManagementAxiosInstance.get(`/orchestrator/${stage}/dependencies`);
        return data;
    }
};

export const TrainerAdministrationAPI = {
    async getDistributorNameForTrainer(trainerEmail: string): Promise<string> {
        const encodedEmail = encodeURIComponent(trainerEmail);
        const { data } = await trainerAdminAxiosInstance.get(
            `/api/trainer/international-partner?email=${encodedEmail}`,
        );
        return data;
    },
};

export const ProfileContractorAPI = {
    /**
     * Fetch the languages that the profile was generated in
     * @param {string} product
     * @param {number} id
     * @returns {Promise<string[]>}
     */
    async fetchLanguages(product: string, id: number | undefined): Promise<string[]> {
        if (id) {
            const { data } = await profileContractorAxiosInstance.get(`/profile/${product}/${id}/languages`);
            return data.languages;
        } else {
            return [];
        }
    },

    async downloadPdf(product: string, id: number, language: string): Promise<Blob> {
        if (getImpersonatedAccount()) {
            throw new Error("Impersonation is not allowed for this request.");
        }
        const response = await profileContractorAxiosInstance.get(`/profile/${product}/${id}?language=${language}`, {
            responseType: "arraybuffer",
            headers: {
                Accept: "application/pdf",
            },
        });

        const data = response.data;
        return new Blob([data], { type: "application/pdf" });
    },

    async authenticatedPdfDownload(
        trainer: string,
        product: string,
        id: number,
        language: string,
    ): Promise<AuthenticatedPdfDownloadResult> {
        if (getImpersonatedAccount()) {
            throw new Error("Impersonation is not allowed for this request.");
        }
        // A project without its full data has no trainer; never send "/profile/undefined/..." (RDT-496).
        if (!trainer) {
            throw new Error("The project's trainer is not loaded yet.");
        }
        const response = await profileContractorAxiosInstance.get(
            `/profile/${trainer}/${product}/${id}?language=${language}`,
            {
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            },
        );

        const data = response.data;
        const blob = new Blob([data], { type: "application/pdf" });
        const filename = extractPdfFilenameFromHeaders(
            response.headers as Record<string, string | undefined>,
            `${product}-${id}-${language}`,
        );

        return { blob, filename };
    },

    async generateProfile(product: string, id: number, hash: string, language: string): Promise<void> {
        const { data } = await profileContractorAxiosInstance.post(
            `/generate/${product}/${id}/${hash}?language=${language}&template=${inDesignTemplate}`,
        );
        return data;
    },

    async generateDirectInput(id: number, directInput: DirectInput, language: string): Promise<void> {
        const associatedDirectInput: AssociatedDirectInput = { id: id, input: directInput };
        const { data } = await profileContractorAxiosInstance.put(`/generate/direct-input/${id}?language=${language}`, associatedDirectInput);
        return data;
    },

    async generateBenchmarkProfile(product: string, projectId: number, language: string): Promise<void> {
        const { data } = await profileContractorAxiosInstance.put(
            `/generate/${product}/${projectId}/group?language=${language}&template=${inDesignTemplate}`,
        );
        return data;
    },

    /**
     * Only recalculate participants values, without regenerating the profile
     * e.g. after changing the benchmark diagram in in123, participants will have a different score
     */
    async recalculateProfileForParticipant(participantId: number): Promise<void> {
        const { data } = await profileContractorAxiosInstance.post(`/generate/calculate/${participantId}`);
        return data;
    },

    /**
     * Lists all group charts for a project, each with its label, members and available languages.
     */
    async getGroupChartsForProject(projectId: number): Promise<GroupChartSummary[]> {
        const { data } = await profileContractorAxiosInstance.get(`/group-chart/project/${projectId}`);
        return data.groupCharts;
    },

    /**
     * Creates a new group chart for the project and returns its summary. An optional label may be
     * supplied; when blank the backend generates a localized default.
     */
    async generateGroupChart(
        product: string,
        projectId: number,
        participants: Participants,
        language: string,
        label?: string,
        withName: boolean = true,
    ): Promise<GroupChartSummary> {
        const labelParam = label ? `&label=${encodeURIComponent(label)}` : "";
        const { data } = await profileContractorAxiosInstance.post(
            `/generate/${product}/${projectId}/group-chart` +
                `?language=${language}&template=${inDesignTemplate}${labelParam}&withName=${withName}`,
            participants,
        );
        return data;
    },

    /**
     * Regenerates a single existing group chart (by its groupChartId) in the given language.
     */
    async regenerateGroupChartById(product: string, groupChartId: number, language: string): Promise<void> {
        const { data } = await profileContractorAxiosInstance.post(
            `/generate/${product}/${groupChartId}/group/regenerate?language=${language}&template=${inDesignTemplate}`,
        );
        return data;
    },

    /**
     * Deletes a single group chart (membership, calculation, direct input and PDFs) by its id.
     */
    async deleteGroupChartById(groupChartId: number): Promise<void> {
        const { data } = await profileContractorAxiosInstance.delete(`/group-chart/${groupChartId}`);
        return data;
    },

    async deleteProfile(id: number, product: string): Promise<void> {
        try {
            const { data } = await profileContractorAxiosInstance.delete(`/profile/${product}/${id}`);
            return data;
        } catch (error) {
            console.warn(`Could not delete profile with id '${id}'.`, error);
        }
    },

    async deleteGroupChart(projectId: number, product: string): Promise<void> {
        const { data } = await profileContractorAxiosInstance.delete(`/profile/${product}/${projectId}`);
        return data;
    },

    async getLevelsForGroupCalculation(groupChartId: number): Promise<AssociatedDirectInput> {
        const { data } = await profileContractorAxiosInstance.post(`/generate/in121/${groupChartId}/group/convert`);
        return data;
    },
};

export const CalculatorAPI = {
    async calculateAndPersistDirectLinkGroup(
        trainerId: string,
        associatedDirectInput: AssociatedDirectInput,
    ): Promise<ProductCalculationResults> {
        const { data } = await calculatorAxiosInstance.put(
            `/group/${trainerId}/${associatedDirectInput.id}/direct`,
            associatedDirectInput,
        );
        return data;
    },
};

export const EmailServiceAPI = {
    /**
     * Sends an email as specified in the parameter.
     * @param email Email object that contains the email content.
     * @param language Request parameter that specifies the language of the email.
     */
    async sendEmail(email: Email, language: string): Promise<void> {
        try {
            const { data } = await emailServiceAxiosInstance.post(`/email?lang=${language}`, email);
            return data;
        } catch {
            console.warn(`Could not send email of type ${email.type} for trainer ${email.trainer.email}.`);
        }
    },

    /**
     * Like {@link sendEmail}, but lets a failure propagate: for emails the trainer sends on purpose
     * and waits for, so the UI can tell them whether it went out (RDT-542).
     */
    async sendEmailReportingErrors(email: Email, language: string): Promise<void> {
        await emailServiceAxiosInstance.post(`/email?lang=${language}`, email);
    },
};

export const DirectInputAPI = {
    async getDirectInputsGroup(trainerId: string, projectIds: number[]): Promise<AssociatedDirectInput[]> {
        const { data } = await directInputAxiosInstance.post(`/direct-input/${trainerId}/group`, projectIds);
        return data.directInputs;
    },

    async saveDirectInputGroup(trainerId: string, associatedDirectInput: AssociatedDirectInput): Promise<AssociatedDirectInput> {
        const { data } = await directInputAxiosInstance.put(
            `/direct-input/group/${trainerId}/${associatedDirectInput.id}`,
            associatedDirectInput,
        );
        return data;
    },

    async getStructureForProduct(product: string): Promise<DirectInput> {
        const { data } = await directInputAxiosInstance.get(`/direct-input/structure/${product}`);
        return data;
    },

    async saveDirectInputForParticipant(participantId: number, trainerId: string, directInput: DirectInput): Promise<void> {
        const data: AssociatedDirectInput = { id: participantId, input: directInput };
        await directInputAxiosInstance.put(`/direct-input/${trainerId}/${participantId}`, data);
    },

};

export const QuestionnaireAPI = {
    async getQuestionnaire(product: string): Promise<Questionnaire> {
        const { data } = await questionnaireAxiosInstance.get(`/questionnaire/${product}`);
        return data;
    },
};
